'use client'

import { useState } from 'react'
import { Radar, Loader2, CheckCircle2, Mail, ArrowRight } from 'lucide-react'
import { trackCtaConversion } from '@/lib/analytics/cta'
import { WATCH_DOMAIN } from '@/lib/analytics/cta-events'

interface WatchDomainCTAProps {
  domain: string
  unifiedScore: number
  unifiedGrade: string
  toolScores?: Array<{ name: string; score: number; grade: string }>
  topActions?: Array<{ title: string; priority: string }>
}

export function WatchDomainCTA({
  domain,
  unifiedScore,
  unifiedGrade,
  toolScores,
  topActions,
}: WatchDomainCTAProps) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<
    'idle' | 'sending' | 'watching' | 'error'
  >('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [nextScanAt, setNextScanAt] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim() || status === 'sending') return

    setStatus('sending')
    setErrorMsg(null)

    try {
      const res = await fetch('/api/platform/watch-domain', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          domain,
          unifiedScore,
          unifiedGrade,
          toolScores: toolScores || [],
          topActions: topActions || [],
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        setErrorMsg(data.message || 'Something went wrong.')
        setStatus('error')
        return
      }

      // Conversion only — the watched domain and the subscriber email stay out
      // of client analytics; watch_domains is the source of truth for both.
      trackCtaConversion(WATCH_DOMAIN.ctaId, WATCH_DOMAIN.event)

      setNextScanAt(data.nextScanAt)
      setStatus('watching')
    } catch {
      setErrorMsg('Network error. Try again.')
      setStatus('error')
    }
  }

  if (status === 'watching') {
    const nextDate = nextScanAt
      ? new Date(nextScanAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })
      : null

    return (
      <div
        className='rounded-xl border p-4 flex items-center gap-3'
        style={{
          background: 'linear-gradient(135deg, #008dcc08, #22c55e08)',
          borderColor: '#22c55e40',
        }}
      >
        <div
          className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
          style={{ background: '#22c55e15' }}
        >
          <CheckCircle2 size={20} style={{ color: 'var(--growth)' }} />
        </div>
        <div className='flex-1 min-w-0'>
          <span className='block text-sm font-semibold text-foreground'>
            Watching {domain}
          </span>
          <span className='block text-xs text-muted-foreground'>
            {nextDate
              ? `Next re-scan ${nextDate}. We'll email you if the score moves.`
              : "We'll email you weekly with score changes."}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div
      className='rounded-xl border p-4'
      style={{
        background: 'linear-gradient(135deg, #008dcc08, #008dcc08)',
        borderColor: '#008dcc30',
      }}
    >
      <div className='flex items-start gap-3 mb-3'>
        <div
          className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
          style={{ background: '#008dcc15' }}
        >
          <Radar size={20} style={{ color: 'var(--primary)' }} />
        </div>
        <div className='flex-1 min-w-0'>
          <span className='block text-sm font-semibold text-foreground leading-tight'>
            Watch {domain}
          </span>
          <span className='block text-xs text-muted-foreground mt-0.5'>
            Weekly re-scan + email when your score moves. Free.
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className='flex items-stretch gap-2'>
        <div className='relative flex-1'>
          <Mail
            size={14}
            className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
          />
          <input
            type='email'
            value={email}
            onChange={e => {
              setEmail(e.target.value)
              setErrorMsg(null)
              if (status === 'error') setStatus('idle')
            }}
            placeholder='your@email.com'
            required
            disabled={status === 'sending'}
            className='w-full h-10 pl-9 pr-3 rounded-lg text-sm outline-none transition-colors focus:ring-1'
            style={{
              background: 'var(--card)',
              border: '1px solid var(--border)',
              color: 'var(--foreground)',
            }}
          />
        </div>
        <button
          type='submit'
          disabled={status === 'sending' || !email.trim()}
          className='h-10 px-4 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-all hover:brightness-110 disabled:opacity-40 flex-shrink-0'
          style={{
            background: 'linear-gradient(135deg, #008dcc, #008dcc)',
            color: '#fff',
          }}
        >
          {status === 'sending' ? (
            <>
              <Loader2 size={14} className='animate-spin' /> Starting…
            </>
          ) : (
            <>
              Start watching <ArrowRight size={14} />
            </>
          )}
        </button>
      </form>

      {errorMsg && (
        <span
          className='block text-[11px] mt-2'
          style={{ color: 'var(--destructive)' }}
        >
          {errorMsg}
        </span>
      )}
    </div>
  )
}
